import React from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ErrorBar,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getColorForSpecies, getWrColorForSpecies, lightenColor } from "../colors";
import { processBinData } from "../processBinData";

export default function NumberFrequencyChart({ graphData, selectedUnit, isMobile, selectedBinSize }) {
  const formatWrPercentTick = (value) => `${(value * 100).toFixed(0)}%`;
  const commonProps = {
    width: "100%",
    height: isMobile ? 500 : 700,
    data: graphData,
    margin: {
      top: isMobile ? 20 : 40,
      right: isMobile ? 20 : 60,
      left: isMobile ? 20 : 80,
      bottom: isMobile ? 80 : 120,
    },
  };
  const binOpts = {
    unitValue: selectedUnit.value,
    binSizeValue: selectedBinSize?.value,
  };
  const processBinDataLocal = (data) => processBinData(data, binOpts);

// Process data into bins by species
const speciesInData = [...new Set(graphData.map((d) => d.species))];

// Process bins for each species separately
const speciesBinData = {};
speciesInData.forEach((species) => {
  const speciesData = graphData.filter((d) => d.species === species);
  const binData = processBinDataLocal(speciesData);
  // Ensure all counts are integers
  speciesBinData[species] = binData.map((bin) => ({
    ...bin,
    number: Math.floor(bin.number),
    culled: Math.floor(bin.culled),
    released: Math.floor(bin.released),
    stocking: Math.floor(bin.stocking || 0),
  }));
});

// Get all unique bin labels across all species
const allBinLabels = [
  ...new Set(
    Object.values(speciesBinData)
      .flat()
      .map((bin) => bin.binLabel)
  ),
].sort((a, b) => {
  // Sort bins by their starting value
  const aStart = parseFloat(a.split("-")[0]);
  const bStart = parseFloat(b.split("-")[0]);
  return aStart - bStart;
});

// Create combined data structure for the chart
const combinedBinData = allBinLabels.map((binLabel) => {
  const binEntry = { binLabel };

  // Initialize counters
  let totalNumber = 0;
  let totalCulled = 0;
  let totalReleased = 0;
  let totalStocked = 0;
  let totalWrSum = 0;
  let totalWrSquaredSum = 0;
  let totalFishCount = 0;

  // Add species-specific data
  speciesInData.forEach((species) => {
    const speciesBin = speciesBinData[species].find(
      (bin) => bin.binLabel === binLabel
    );
    if (speciesBin) {
      binEntry[`${species}_number`] = Math.floor(speciesBin.number);
      binEntry[`${species}_culled`] = Math.floor(speciesBin.culled);
      binEntry[`${species}_released`] = Math.floor(speciesBin.released);
      binEntry[`${species}_stocked`] = Math.floor(speciesBin.stocking || 0);
      binEntry[`${species}_averageWr`] = speciesBin.averageWr;
      binEntry[`${species}_stdev`] = speciesBin.stdev;

      // Add to totals for overall average
      totalNumber += Math.floor(speciesBin.number);
      totalCulled += Math.floor(speciesBin.culled);
      totalReleased += Math.floor(speciesBin.released);
      totalStocked += Math.floor(speciesBin.stocking || 0);
      totalWrSum +=
        speciesBin.averageWr * Math.floor(speciesBin.number);
      totalFishCount += Math.floor(speciesBin.number);
    } else {
      binEntry[`${species}_number`] = 0;
      binEntry[`${species}_culled`] = 0;
      binEntry[`${species}_released`] = 0;
      binEntry[`${species}_stocked`] = 0;
      binEntry[`${species}_averageWr`] = 0;
      binEntry[`${species}_stdev`] = 0;
    }
  });

  // Calculate overall averages for this bin
  binEntry.totalNumber = Math.floor(totalNumber);
  binEntry.totalCulled = Math.floor(totalCulled);
  binEntry.totalReleased = Math.floor(totalReleased);
  binEntry.totalStocked = Math.floor(totalStocked);
  binEntry.overallAverageWr =
    totalFishCount > 0 ? totalWrSum / totalFishCount : 0;

  return binEntry;
});

// Check if we have culled/released data
const hasCulled = combinedBinData.some((bin) =>
  speciesInData.some((species) => bin[`${species}_culled`] > 0)
);
const hasReleased = combinedBinData.some((bin) =>
  speciesInData.some((species) => bin[`${species}_released`] > 0)
);
const hasStocked = combinedBinData.some((bin) =>
  speciesInData.some((species) => bin[`${species}_stocked`] > 0)
);

// --- Dynamic Tick Synchronization ---
const numTicks = 8;
const leftMin = 0;
const leftMax = Math.max(
  ...combinedBinData.map((bin) => bin.totalNumber),
  8
);
const leftStep = (leftMax - leftMin) / (numTicks - 1);
const leftTicks = Array.from(
  { length: numTicks },
  (_, i) => leftMin + i * leftStep
);

const rightMin = 0.2;
const rightMax = Math.max(
  ...combinedBinData.map((bin) => bin.overallAverageWr),
  1.8
);
const rightStep = (rightMax - rightMin) / (numTicks - 1);
const rightTicks = Array.from(
  { length: numTicks },
  (_, i) => rightMin + i * rightStep
);
// --- End Dynamic Tick Synchronization ---

// Calculate y-axis ticks ensuring equal number of horizontal grid lines on left and right
const leftMaxNumObserved = Math.max(
  ...combinedBinData.map((bin) => bin.totalNumber),
  0
);
const rightMaxAvgObserved = Math.max(
  ...combinedBinData.map((bin) => bin.overallAverageWr),
  0
);

// Start with target of 10 ticks; adjust to satisfy data on right axis (0.2 steps)
const baseTickCount = 10;
const wrStep = 0.2;
const neededRightTicks = Math.floor(Math.ceil((rightMaxAvgObserved + 1e-9) / wrStep)) + 1; // 0..ceil(max/0.2)
const targetTickCount = Math.max(baseTickCount, neededRightTicks);

// Left axis (Number of Fish): choose integer step so that tickCount equals targetTickCount and covers data
const leftSegments = targetTickCount - 1;
const fishStep = Math.max(1, Math.ceil(leftMaxNumObserved / leftSegments));
const leftUpper = fishStep * leftSegments;
const leftTicksNum = [];
for (let v = 0; v <= leftUpper; v += fishStep) leftTicksNum.push(v);

// Right axis (Avg Wr): fixed 0.2 step to match same number of ticks
const rightUpper = wrStep * leftSegments;
const rightTicksAvg = [];
for (let v = 0; v <= rightUpper + 1e-9; v += wrStep) rightTicksAvg.push(Number(v.toFixed(2)));
if (!rightTicksAvg.includes(1)) rightTicksAvg.push(1);
rightTicksAvg.sort((a, b) => a - b);

// Calculate total culled/released for legend
const speciesTotals = {};
speciesInData.forEach((species) => {
  speciesTotals[species] = {
    culled: combinedBinData.reduce(
      (sum, bin) => sum + (bin[`${species}_culled`] || 0),
      0
    ),
    released: combinedBinData.reduce(
      (sum, bin) => sum + (bin[`${species}_released`] || 0),
      0
    ),
    stocked: combinedBinData.reduce(
      (sum, bin) => sum + (bin[`${species}_stocked`] || 0),
      0
    ),
  };
});

return (
  <ResponsiveContainer {...{ ...commonProps, height: isMobile ? 600 : 750 }}>
    <ComposedChart data={combinedBinData}>
      <CartesianGrid strokeDasharray="3 3" stroke="#000000" strokeWidth={1} />

      {/* Custom background highlighting for empty bins */}
      <defs>
        <pattern id="emptyBinPattern" patternUnits="userSpaceOnUse" width="4" height="4">
          <rect width="4" height="4" fill="#ffebee" />
          <path d="m0,4l4,-4M-1,1l2,-2M3,5l2,-2" stroke="#ffcdd2" strokeWidth="0.5" />
        </pattern>
      </defs>

      <XAxis
        dataKey="binLabel"
        label={{
          value: `Length Bin (${selectedUnit.value === "English" ? "Inches" : "mm"
            })`,
          position: "outsideBottom",
          offset: isMobile ? 20 : 25,
          fontSize: isMobile ? 14 : 18,
          fontWeight: 'bold',
          fill: '#000000'
        }}
        angle={-90}
        textAnchor="end"
        height={isMobile ? 160 : 200}
        interval={0}
        tickLine={false}
        allowDuplicatedCategory={false}
        stroke="#000000"
        strokeWidth={2}
        tick={({ x, y, payload }) => {
          // Find the bin data to check if it's empty
          const binData = combinedBinData.find(bin => bin.binLabel === payload.value);
          const isEmpty = binData && binData.totalNumber === 0;

          return (
            <text
              x={x}
              y={y + 12}
              textAnchor="end"
              fontSize={isMobile ? 12 : 14}
              fontWeight="bold"
              fill={isEmpty ? "#ff6b6b" : "#000000"}
              transform={`rotate(-90, ${x}, ${y + 10})`}
            >
              {payload.value}
            </text>
          );
        }}
      />

      <YAxis
        yAxisId="left"
        label={{
          value: "Number of Fish",
          angle: -90,
          position: "insideLeft",
          style: { textAnchor: "middle" },
          fontSize: isMobile ? 16 : 20,
          fontWeight: 'bold',
          fill: '#000000'
        }}
        domain={[0, leftTicksNum[leftTicksNum.length - 1] || 0]}
        tick={{ fontSize: isMobile ? 12 : 14, fontWeight: 'bold', fill: '#000000' }}
        ticks={leftTicksNum}
        tickFormatter={(v) => (Number.isInteger(v) ? v : "")}
        stroke="#000000"
        strokeWidth={2}
      />

      <YAxis
        yAxisId="right"
        orientation="right"
        label={{
          value: "Average Wr (%)",
          angle: -90,
          position: "insideRight",
          style: { textAnchor: "middle" },
          fontSize: isMobile ? 16 : 20,
          fontWeight: 'bold',
          fill: '#000000'
        }}
        domain={[0, rightTicksAvg[rightTicksAvg.length - 1] || 0]}
        tick={{ fontSize: isMobile ? 12 : 14, fontWeight: 'bold', fill: '#000000' }}
        ticks={rightTicksAvg}
        tickFormatter={formatWrPercentTick}
        stroke="#000000"
        strokeWidth={2}
      />

      <Tooltip
        content={({ active, payload, label }) => {
          if (!active || !payload || !payload.length) return null;
          const bin = payload[0].payload;

          // Filter out species with no data
          const validSpecies = speciesInData.filter(species =>
            bin[`${species}_number`] > 0
          );

          return (
            <div className="custom-tooltip" style={{
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: '2px',
              padding: '8px 10px',
              fontSize: '13px',
              lineHeight: '1.5',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              minWidth: '220px'
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px',
                borderBottom: '1px solid #eee',
                paddingBottom: '4px'
              }}>
                Length Bin: {bin.binLabel} {selectedUnit.value === "English" ? "Inches" : "mm"}
              </div>

              <div style={{ display: 'grid', gap: '8px' }}>
                {validSpecies.map(species => {
                  const speciesColor = getColorForSpecies(species, speciesInData);
                  return (
                    <div key={species} style={{
                      borderLeft: `3px solid ${speciesColor}`,
                      paddingLeft: '8px'
                    }}>
                      <div style={{
                        fontWeight: '500',
                        color: speciesColor,
                        marginBottom: '2px'
                      }}>
                        {species}
                      </div>

                      <div style={{
                        display: 'grid',
                        gap: '2px',
                        fontSize: '12px',
                        color: '#666'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Number:</span>
                          <span style={{ fontWeight: '500' }}>
                            {bin[`${species}_number`]}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Culled:</span>
                          <span>{bin[`${species}_culled`]}</span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Released:</span>
                          <span>{bin[`${species}_released`]}</span>
                        </div>
                        {hasStocked && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Stocked:</span>
                            <span>{bin[`${species}_stocked`]}</span>
                          </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Avg Wr:</span>
                          <span style={{
                            color: bin[`${species}_averageWr`] >= 1.0 ? '#2ecc71' : '#e74c3c',
                            fontWeight: '500'
                          }}>
                            {(Number(bin[`${species}_averageWr`]) * 100).toFixed(1)}%
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>StdDev:</span>
                          <span>{bin[`${species}_stdev`]?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {validSpecies.length > 1 && (
                  <div style={{
                    marginTop: '2px',
                    paddingTop: '6px',
                    borderTop: '1px solid #eee'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontWeight: '500'
                    }}>
                      <span>Total Fish:</span>
                      <span>{bin.totalNumber}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      color: bin.overallAverageWr >= 1.0 ? '#2ecc71' : '#e74c3c'
                    }}>
                      <span>Overall Avg Wr:</span>
                      <span>{(Number(bin.overallAverageWr) * 100).toFixed(1)}%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }}
      />

      <Legend
        layout="horizontal"
        verticalAlign="top"
        align="center"
        wrapperStyle={{
          paddingBottom: isMobile ? 20 : 40,
          fontSize: isMobile ? 14 : 17,
          display: "flex",
          flexDirection: "column", // Stack species vertically
          alignItems: "flex-start", // Align items to the left
          width: "100%",
        }}
        content={(props) => {
          const { payload } = props;
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              {speciesInData.map((species) => (
                <div
                  key={species}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "8px",
                    width: "100%",
                  }}
                >
                  {/* Culled */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: "20px",
                    }}
                  >
                    <div
                      style={{
                        width: "14px",
                        height: "14px",
                        backgroundColor: getColorForSpecies(
                          species,
                          speciesInData
                        ),
                        marginRight: "4px",
                      }}
                    />
                    <span>{`${species} Culled (${speciesTotals[species].culled})`}</span>
                  </div>

                  {/* Released */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: "20px",
                    }}
                  >
                    <div
                      style={{
                        width: "14px",
                        height: "14px",
                        backgroundColor: lightenColor(
                          getColorForSpecies(species, speciesInData),
                          50
                        ),
                        opacity: 0.7,
                        marginRight: "4px",
                      }}
                    />
                    <span>{`${species} Released (${speciesTotals[species].released})`}</span>
                  </div>
                  {hasStocked && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginRight: "20px",
                      }}
                    >
                      <div
                        style={{
                          width: "14px",
                          height: "14px",
                          backgroundColor: "#8b5cf6",
                          marginRight: "4px",
                        }}
                      />
                      <span>{`${species} Stocked (${speciesTotals[species].stocked})`}</span>
                    </div>
                  )}

                  {/* Wr */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        width: "14px",
                        height: "2px",
                        backgroundColor: getWrColorForSpecies(species, speciesInData),
                        marginRight: "4px",
                      }}
                    />
                    <span>{`${species} Wr (%)`}</span>
                  </div>
                </div>
              ))}
            </div>
          );
        }}
      />

      {/* Static horizontal line at Wr = 1.0 */}
      <ReferenceLine
        y={1.0}
        yAxisId="right"
        stroke="#e74c3c"
        strokeWidth={2}
        strokeDasharray="3 3"
      />

      {/* Render bars for each species */}
      {speciesInData.map((species, index) => {
        const speciesColor = getColorForSpecies(species, speciesInData);
        const wrLineColor = getWrColorForSpecies(species, speciesInData);
        const lightColor = lightenColor(speciesColor, 50);
        const stockedColor = "#8b5cf6";

        return [
          <Bar
            key={`${species}-culled`}
            yAxisId="left"
            dataKey={`${species}_culled`}
            name={`${species} Culled`}
            stackId={`stack_${index}`}
            fill={speciesColor}
            barSize={30}
            label={{
              position: "inside",
              fill: "#fff",
              fontSize: 12,
              formatter: (value) =>
                value > 0 ? Math.round(value) : "",
            }}
          />,
          <Bar
            key={`${species}-released`}
            yAxisId="left"
            dataKey={`${species}_released`}
            name={`${species} Released`}
            stackId={`stack_${index}`}
            fill={lightColor}
            fillOpacity={0.7}
            barSize={30}
            label={{
              position: "top",
              fill: "#000",
              fontSize: 12,
              fontWeight: "bold",
              formatter: (value) =>
                value > 0 ? Math.round(value) : "",
              offset: 10,
            }}
          />,
          ...(hasStocked
            ? [
                <Bar
                  key={`${species}-stocked`}
                  yAxisId="left"
                  dataKey={`${species}_stocked`}
                  name={`${species} Stocked`}
                  stackId={`stack_${index}`}
                  fill={stockedColor}
                  barSize={30}
                  label={{
                    position: "top",
                    fill: "#000",
                    fontSize: 12,
                    fontWeight: "bold",
                    formatter: (value) =>
                      value > 0 ? Math.round(value) : "",
                    offset: 10,
                  }}
                />,
              ]
            : []),
        ];
      })}

      {/* Wr line for each species with error bars */}
      {speciesInData.map((species, idx) => {
        const wrLineColor = getWrColorForSpecies(species, speciesInData);
        return (
          <Line
            key={`${species}-wr`}
            yAxisId="right"
            type="linear"
            dataKey={`${species}_averageWr`}
            name={`${species} Wr (%)`}
            stroke={wrLineColor}
            strokeWidth={2}
            dot={{ fill: wrLineColor, strokeWidth: 2, r: 4 }}
          >
            <ErrorBar
              dataKey={`${species}_stdev`}
              width={4}
              stroke={wrLineColor}
              strokeWidth={1}
            />
          </Line>
        );
      })}

    </ComposedChart>
  </ResponsiveContainer>
);
}
